import { ThemeOptions } from '@mui/material/styles'
import { darkTheme, defaultTheme, Theme as Av8Theme } from './theme'
import { Input } from '../components/Input'
import { KeyboardArrowDown } from '@mui/icons-material'
import * as React from 'react'

declare module '@mui/material/styles' {
  export interface Theme {
    av8: Av8Theme
  }
  // allow configuration using `createTheme`
  export interface ThemeOptions {
    av8: Av8Theme
  }
}

function generateMuiThemeOptions(t: Av8Theme): ThemeOptions {
  const buttonHoverEffect = {
    '&:active': {
      background: t.backgroundAlt
    },
    '&:focus': {
      background: t.backgroundAlt
    },
    '&:hover': {
      background: `${t.backgroundAlt}!important`
    }
  }

  const buttonTextHoverEffect = buttonHoverEffect

  const options: ThemeOptions = {
    components: {
      MuiAccordion: {
        styleOverrides: {
          root: {
            padding: 0,
            borderBottom: 0,
            backgroundColor: t.background,
            '&.Mui-expanded': {
              margin: 0
            },
            '&::before': {
              display: 'none'
            }
          },
          expanded: {}
        }
      },
      MuiAccordionDetails: {
        styleOverrides: {
          root: {
            padding: '32px 32px 32px 40px',
            flexDirection: 'column',
            gap: '24px'
          }
        }
      },
      MuiAccordionSummary: {
        styleOverrides: {
          root: {
            margin: 0,
            padding: '8px 0',
            minHeight: 'initial',
            backgroundColor: t.background,
            '&.Mui-expanded': {
              minHeight: 'initial',
              background: t.backgroundAlt
            }
          },
          content: {
            minHeight: 'initial',
            margin: 0,
            padding: 0,
            '&.Mui-expanded': {
              margin: 0
            }
          },
          expanded: {},
          expandIconWrapper: {
            marginRight: '8px'
          }
        }
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            padding: '32px 0 24px 0',
            boxShadow: 'none',
            backgroundColor: `${t.background}!important`,
            flexGrow: 1
          }
        }
      },
      MuiBackdrop: {
        styleOverrides: {
          root: {
            backgroundColor: t.backdropColor
          }
        }
      },
      MuiButton: {
        defaultProps: {
          variant: 'outlined'
        },
        styleOverrides: {
          root: {
            fontWeight: 500,
            textTransform: 'uppercase',
            fontSize: '12px',
            letterSpacing: '2px',
            lineHeight: '1.5rem'
          },
          outlined: {
            padding: '11px 18px',
            borderColor: t.primaryColor,
            minWidth: '100px',
            ...buttonHoverEffect
          },
          text: {
            padding: '11px 18px',
            ...buttonTextHoverEffect
          }
        }
      },
      MuiButtonBase: {
        defaultProps: {
          disableRipple: true
        },
        styleOverrides: {}
      },
      MuiButtonGroup: {
        defaultProps: {
          disableRipple: true,
          variant: 'text'
        }
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 4
          }
        }
      },
      MuiCardActionArea: {
        styleOverrides: {
          root: {
            '& > span': {
              background: t.background
            },
            '&:hover': {
              '& > span': {
                opacity: '0.2!important'
              }
            }
          }
        }
      },
      MuiCardMedia: {
        styleOverrides: {
          root: {
            borderRadius: 4
          }
        }
      },
      MuiCheckbox: {
        styleOverrides: {
          root: {
            backgroundColor: 'transparent!important'
          }
        }
      },
      MuiRadio: {
        styleOverrides: {
          root: {
            backgroundColor: 'transparent!important'
          }
        }
      },
      MuiDialog: {
        styleOverrides: {
          root: {
            "& [class^='MuiBackdrop-root']": {
              backgroundColor: t.backdropColor
            }
          }
        }
      },
      MuiDialogActions: {
        styleOverrides: {
          root: {
            padding: '0 24px 24px 24px',
            '& button:not(:first-of-type)': {
              marginLeft: '24px'
            }
          }
        }
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            padding: '24px 24px 16px 24px',
            fontFamily: t.typography.headers.fontFamily,
            '& h2': {
              fontFamily: 'inherit'
            }
          }
        }
      },
      MuiDialogContent: {
        styleOverrides: {
          root: {
            boxSizing: 'border-box'
          }
        }
      },
      MuiFormControl: {
        styleOverrides: {
          root: {
            '& .MuiInputBase-root': {
              marginTop: '0!important'
            }
          }
        }
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            '&:hover': {
              backgroundColor: t.backgroundAlt
            }
          }
        }
      },
      MuiInput: {
        styleOverrides: {
          underline: {
            '&::after': {
              display: 'none'
            },
            '&::before': {
              display: 'none'
            }
          }
        }
      },
      MuiAutocomplete: {
        defaultProps: {
          popupIcon: React.createElement(KeyboardArrowDown, { style: { color: t.primaryColor } })
        },
        styleOverrides: {
          root: {
            '& .MuiAutocomplete-inputRoot': {
              padding: '0 16px!important'
            },
            '& .MuiAutocomplete-inputRoot > span:last-of-type': {
              marginRight: '5px'
            },
            '& .MuiAutocomplete-inputRoot input': {
              padding: '11px 16px 11px 1px!important',
              lineHeight: '1.5rem',
              minHeight: '1.5rem'
            },
            '& .MuiAutocomplete-endAdornment': {
              right: '8px'
            },
            '& .MuiAutocomplete-startAdornment': {
              right: '8px'
            },
            '&.Mui-focused:not(.Mui-disabled)': {
              '& .MuiAutocomplete-inputRoot input': {
                padding: '10px 15px 10px 0!important'
              },
              '& .MuiAutocomplete-endAdornment': {
                right: '7px'
              },
              '& .MuiInputAdornment-startAdornment': {
                left: '7px'
              }
            },
            '&.Mui-disabled': {
              opacity: 1,
              color: t.secondaryColor,
              cursor: 'text'
            }
          },
          option: {
            backgroundColor: t.background,
            '&.Mui-focused': {
              backgroundColor: `${t.backgroundAlt}!important`
            }
          },
          listbox: {
            backgroundColor: t.background,
            '& .MuiAutocomplete-option[aria-selected="true"]': {
              backgroundColor: t.background
            }
          }
        }
      },
      MuiInputAdornment: {
        styleOverrides: {
          root: {
            position: 'relative',
            '&.MuiInputAdornment-positionEnd': {
              marginRight: '8px',
              marginLeft: '0'
            },
            '&.MuiInputAdornment-positionStart': {
              marginLeft: '8px',
              marginRight: '0'
            }
          }
        }
      },
      MuiInputBase: {
        defaultProps: {},
        styleOverrides: {
          root: {
            '& > input, .MuiSelect-select': {
              padding: '11px 16px!important'
            },
            color: t.input.color,
            borderRadius: '4px',
            backgroundColor: t.background,
            width: '100%',
            margin: 0,
            paddingBottom: '0!important',
            border: `solid 1px ${t.input.borderColor}!important`,
            '&.Mui-disabled': {
              opacity: '0.5'
            },
            '&:hover:not(.Mui-disabled)': {
              borderColor: `${t.primaryColor}!important`
            },
            '&.Mui-focused': {
              backgroundColor: t.backgroundAlt,
              border: `solid 2px ${t.primaryColor}!important`,
              '& > input, .MuiSelect-select': {
                padding: '10px 15px!important'
              },
              '.MuiSelect-icon': {
                margin: 'translateX(1px)',
                right: 6
              },
              '.MuiInputAdornment-positionEnd': {
                left: '1px'
              },
              '.MuiInputAdornment-positionStart': {
                left: '-1px'
              }
            },
            '&.Mui-error': {
              borderColor: `${t.dangerColor}!important`
            },
            fontSize: '14px',
            fieldset: {
              borderWidth: '0px!important',
              top: 0,
              legend: {
                display: 'none'
              }
            }
          },
          input: {
            '&::placeholder': {
              opacity: 0.4
            },
            '&:before, &:after': {
              display: 'none!important'
            },
            minHeight: '1.5rem',
            lineHeight: '1.5rem'
          },
          multiline: {
            overflow: 'hidden',
            padding: 0,
            textarea: {
              padding: '11px 10px 11px 16px!important'
            },
            '&.Mui-focused': {
              textarea: {
                margin: '-1px!important'
              }
            }
          }
        }
      },
      MuiInputLabel: {
        defaultProps: {
          shrink: false
        },
        styleOverrides: {
          root: {
            color: t.input.label.color,
            textTransform: 'uppercase',
            marginBottom: '5px!important',
            letterSpacing: '2px',
            fontSize: '10px!important',
            position: 'static!important' as any,
            transform: 'none!important' as any,
            '&.Mui-error': {
              color: t.input.label.color
            }
          }
        }
      },
      MuiFormControlLabel: {
        styleOverrides: {
          root: {
            '& span': {
              color: t.input.label.color,
              fontSize: '14px'
            },
            marginLeft: 0
          },
          label: {
            marginLeft: '0.5rem'
          }
        }
      },
      MuiFormHelperText: {
        styleOverrides: {
          root: {
            marginLeft: 0,
            fontSize: 10,
            textTransform: 'uppercase',
            letterSpacing: '2px',
            '&.Mui-error': {
              color: t.dangerColor
            }
          }
        }
      },
      MuiList: {
        defaultProps: {
          disablePadding: true
        }
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderBottom: `solid 1px ${t.dividerColor}`,
            padding: '16px 26px!important',
            '& svg': {
              color: t.primaryColor
            },
            '& span': {
              color: t.primaryColor
            },
            '&.Mui-selected': {
              backgroundColor: t.backgroundAlt
            },
            ...buttonHoverEffect
          }
        }
      },
      MuiCircularProgress: {
        styleOverrides: {
          circle: {
            stroke: t.primaryColor
          }
        }
      },
      MuiMenu: {
        defaultProps: {
          elevation: 2
        }
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            borderBottom: `solid 1px ${t.dividerColor}`,
            padding: '20px 26px!important',
            '& svg': {
              color: t.primaryColor
            },
            '& span': {
              color: t.primaryColor
            },
            '&.Mui-selected': {
              backgroundColor: t.backgroundAlt
            },
            ...buttonHoverEffect
          }
        }
      },
      MuiSelect: {
        defaultProps: {
          input: React.createElement(Input),
          IconComponent: KeyboardArrowDown
        },
        styleOverrides: {
          select: {
            color: t.secondaryColor,
            '&:focus': {
              backgroundColor: `${t.backgroundAlt}!important`
            }
          },
          icon: {
            width: '2.4rem',
            color: t.primaryColor
          }
        }
      },
      MuiTextField: {
        defaultProps: {
          variant: 'standard' //we don't want the fieldset created by the outlined variant
        }
      }
    },
    shape: {
      borderRadius: 4
    },
    typography: {
      body1: {
        color: t.primaryColor
      },
      body2: {
        color: t.secondaryColor
      },
      fontFamily: t.typography.fontFamily,
      h1: {
        fontFamily: t.typography.headers.fontFamily,
        color: t.primaryColor
      },
      h2: {
        fontFamily: t.typography.headers.fontFamily,
        color: t.primaryColor
      },
      h3: {
        fontFamily: t.typography.headers.fontFamily,
        color: t.primaryColor
      },
      h4: {
        fontFamily: t.typography.headers.fontFamily,
        color: t.primaryColor
      },
      h5: {
        fontFamily: t.typography.headers.fontFamily,
        color: t.primaryColor
      },
      h6: {
        fontFamily: t.typography.headers.fontFamily,
        color: t.primaryColor
      }
    },
    shadows: [
      'none',
      ...Array.from({ length: 24 }, () => `0px 0px 40px 0px rgba(0,0,0, ${t.isDark ? '0.7' : 0.1})`)
    ] as any,
    palette: {
      mode: t.isDark ? 'dark' : 'light',
      primary: {
        main: t.primaryColor
      },
      secondary: {
        main: t.secondaryColor
      },
      error: { main: t.dangerColor },
      success: { main: t.successColor },
      text: {
        primary: t.primaryColor,
        secondary: t.secondaryColor,
        disabled: '#C4C4C4' //from a design - need review
      },
      warning: {
        main: t.dangerColor
      },
      divider: '#C4C4C4',
      background: {
        default: t.background,
        paper: t.background
      }
    },
    av8: defaultTheme
  }
  return options
}

export const muiDefaultTheme = generateMuiThemeOptions(defaultTheme)
export const muiDarkTheme = generateMuiThemeOptions(darkTheme)
